import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { useNavigate, useParams } from 'react-router'
import ContentHeader from '@Components/ContentHeader'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import { StoresContext } from '@App/contexts'
import { PARAM } from '@App/navigation/param'
import { PublisherSideBarState } from '@App/enum/publisher-side-bar-state'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import { createSongwriterOriginalPublisherSubmissionSuggestion } from '@App/api/routes/songwriter/songwriter'
import Navigation from '@App/navigation/index'
import SongwriterWorkShareUpdateRequestTable from './SongwriterWorkShareUpdateRequestTable'
import ShareUpdateRequestActions from './ShareUpdateRequestActions'
import SubmissionSuccessModal from './SubmissionSuccessModal'
import PublisherSidebarModal from './PublisherSidebarModal'
import { isValidSuggestionPublisher } from './SuggestionPublisher'
import './index.scss'

const instructions = (
    <p>
        To request an update for a missing or incorrect share, click Edit Share next to the share you want to change.
        Enter the correct share amount, then click Submit Request. Your request will be sent to your
        publisher/administrator. Any updates or changes will be determined by their review process.
    </p>
)

const SongwriterWorkShareUpdateRequestScene = () => {
    const { publicViewWorkStore, popupStore, publisherStepStore, workStore, preloaderStore, songwriterStore } =
        useContext(StoresContext)
    const params = useParams<typeof PARAM>()
    const navigate = useNavigate()
    const [selectedOriginalPublisher, setSelectedOriginalPublisher] = useState<OriginalPublisherModel>()
    const [isSelectedOriginalPublisherValid, setIsSelectedOriginalPublisherValid] = useState(true)
    const [isAddingNewPublisherRow, setIsAddingNewPublisherRow] = useState(false)
    const [displaySubmissionPopup, setDisplaySubmissionPopup] = useState(false)

    const work = publicViewWorkStore.viewWorkPublic
    const sidebarState = popupStore.publisherSidebarState
    const title = work ? `Request to Update Shares for “${work.title}”` : 'Request to Update Shares'
    const originalPublisherCount = work?.originalPublishers.length ?? 0
    const songwriterId = songwriterStore.currentSongwriterId
    const suggestionPublisher =
        selectedOriginalPublisher ??
        (isAddingNewPublisherRow ? workStore.workDraftLocal?.originalPublishers?.at(-1) : undefined)

    useEffect(() => {
        const workId = params.workId
        if (workId) {
            void publicViewWorkStore.getWorkCatalogById(workId)
        }

        return () => publicViewWorkStore.resetViewWorkPublic()
    }, [params.workId])

    useEffect(() => {
        if (work) {
            workStore.setWorkDraftToLocal(work)
        }
    }, [work])

    useEffect(() => {
        if (!isAddingNewPublisherRow) {
            return
        }

        const draftOriginalPublishers = workStore.workDraftLocal?.originalPublishers
        if (!draftOriginalPublishers?.length || draftOriginalPublishers.length <= originalPublisherCount) {
            return
        }

        const latestPublisher = draftOriginalPublishers[draftOriginalPublishers.length - 1]
        setSelectedOriginalPublisher(latestPublisher)
        setIsSelectedOriginalPublisherValid(isValidSuggestionPublisher(latestPublisher))
    }, [isAddingNewPublisherRow, originalPublisherCount, workStore.workDraftLocal])

    const closePublisherSidebar = () => popupStore.setPublisherSidebarState(PublisherSideBarState.empty)
    const openAddPublisherSidebar = () => popupStore.setPublisherSidebarState(PublisherSideBarState.addOP)
    const openAddAdministratorSidebar = (publisher: OriginalPublisherModel) => publisherStepStore.initAddAP(publisher)
    const handleSuggestionChanged = (publisher: OriginalPublisherModel | undefined, isValid: boolean) => {
        setSelectedOriginalPublisher(publisher)
        setIsSelectedOriginalPublisherValid(isValid)
    }

    const addNewRow = () => {
        setSelectedOriginalPublisher(undefined)
        setIsSelectedOriginalPublisherValid(false)
        setIsAddingNewPublisherRow(true)
    }

    const submitRequest = async () => {
        const workPropertyId = params.workId
        const administratorPublisher = suggestionPublisher?.administratorPublishers[0]
        const originalPublisherIpId = suggestionPublisher?.ipId
        const administratorPublisherIpId = administratorPublisher?.ipId
        const share = administratorPublisher?.publisherShare ?? suggestionPublisher?.publisherShare
        if (!songwriterId || !workPropertyId || !originalPublisherIpId || share === undefined || share === null) {
            return
        }

        const originalPublisherIpIdParam = Number(originalPublisherIpId)
        const administratorPublisherIpIdParam = administratorPublisherIpId
            ? Number(administratorPublisherIpId)
            : undefined

        preloaderStore.openTransparentPreloader(true)
        try {
            await createSongwriterOriginalPublisherSubmissionSuggestion(
                songwriterId,
                workPropertyId,
                originalPublisherIpIdParam,
                share,
                administratorPublisherIpIdParam
            )
            setDisplaySubmissionPopup(true)
            setIsAddingNewPublisherRow(false)
            setIsSelectedOriginalPublisherValid(true)
        } catch (error) {
            popupStore.setAlertFromError(error)
        } finally {
            preloaderStore.openTransparentPreloader(false)
        }
    }

    const onConfirm = () => {
        setDisplaySubmissionPopup(false)
        navigate(Navigation.songwriter.catalog.root(songwriterId))
    }

    return (
        <>
            <title>Request to Update Shares</title>
            <div className="songwriter-work-share-update-request-scene">
                <ContentHeader title={title} content={instructions} />
                {work ? (
                    <>
                        <SongwriterWorkShareUpdateRequestTable
                            originalPublishers={workStore.workDraftLocal?.originalPublishers ?? []}
                            existingPublisherCount={originalPublisherCount}
                            onAddPublisher={openAddPublisherSidebar}
                            onAddAdministrator={openAddAdministratorSidebar}
                            onSuggestionChanged={handleSuggestionChanged}
                            onAddRow={addNewRow}
                        />
                        <ShareUpdateRequestActions
                            disabled={!suggestionPublisher || !isSelectedOriginalPublisherValid}
                            onBack={() => navigate(-1)}
                            onSubmit={submitRequest}
                        />
                    </>
                ) : (
                    <PointPreloader />
                )}
                <SubmissionSuccessModal isOpen={displaySubmissionPopup} onConfirm={onConfirm} />
                <PublisherSidebarModal sidebarState={sidebarState} onClose={closePublisherSidebar} />
            </div>
        </>
    )
}

export default observer(SongwriterWorkShareUpdateRequestScene)
